// src/components/accordion/AccSecurity.tsx

import { ExpandMoreTwoTone } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  List,
  Typography,
} from '@mui/material'
import { Dispatch, SetStateAction } from 'react'

import { FolderId, SensorRecord, SensorId, SensorType } from '@/types/domain'

import { CorrelationSecurityItem, HStack } from '..'
import {
  colorSchemeSecurity,
  normalizedSensorType as nst,
  sensorType,
} from '../../api'
import { useFolderSensors, useSecuritySensors } from '../../hooks'

type AccSecurityProps = {
  data: {
    baseline: Array<any>
    medium: SensorType
    selected: Array<SensorId>
  }
  id: FolderId
  onChange: Dispatch<SetStateAction<Array<SensorId>>>
  onChangeBaseline: Dispatch<SetStateAction<Array<SensorId>>>

  changeFeelingSensors: (id: SensorId) => void
  setFeelingSensorId: (id: SensorId) => void
}

export const AccSecurity = ({
  data,
  id,
  onChange,
  onChangeBaseline,

  changeFeelingSensors,
  setFeelingSensorId,
}: AccSecurityProps) => {
  const { baseline, medium, selected } = data

  const securitySensors: SensorRecord[] = useSecuritySensors(id)
  const fSensors: SensorRecord[] = useFolderSensors(id)

  const sSensors = securitySensors.filter(s => nst(s.sensor_type) === medium)

  const ALL_SELECTED = sSensors.every(s => selected.includes(s.entity_id))
  const SOME_SELECTED = sSensors.some(s => selected.includes(s.entity_id))

  const onClick = event => {
    event.stopPropagation()

    let _selected = [...selected]
    let _baseline = [...baseline]

    sSensors.forEach(s => {
      const sId: SensorId = Number(s.entity_id)

      if (!ALL_SELECTED) {
        _selected.push(sId)
      } else {
        _selected = _selected.filter(id => id !== sId)
        _baseline = _baseline.filter(f => f.entity_id !== sId)
      }
    })

    onChange(_selected)
    onChangeBaseline(_baseline)
  }

  const borderLeft = `solid 10px ${colorSchemeSecurity[0]}`

  const FILTER_MEDIUM = f =>
    nst(f.sensor_type, f.type_in_folder) === medium ||
    nst(f.sensor_type, f.type_in_folder) === `${medium}_detail`

  const isSelected = (id: SensorId) => selected.includes(id)

  return (
    <Accordion sx={{ '&.Mui-expanded': { m: '0 !important' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreTwoTone />}
        sx={{ borderLeft, justifyContent: 'space-between' }}
      >
        <HStack justifyContent={'space-between'} sx={{ width: 1 }}>
          <Typography>{sensorType(medium)}</Typography>

          <Checkbox
            checked={ALL_SELECTED}
            color={'primary'}
            indeterminate={SOME_SELECTED && !ALL_SELECTED}
            onClick={onClick}
          />
        </HStack>
      </AccordionSummary>

      <AccordionDetails sx={{ borderLeft, p: 0 }}>
        <List sx={{ width: 1 }}>
          <Divider />

          {sSensors.filter(FILTER_MEDIUM).map(s => {
            const sId: SensorId = Number(s.entity_id)

            const onClick = event => {
              event.stopPropagation()

              const record: SensorRecord = fSensors.find(
                f => f.entity_id === sId
              )
              if (!record) return

              setFeelingSensorId(sId)
              changeFeelingSensors(sId)
            }

            return (
              <CorrelationSecurityItem
                key={sId}
                onClick={onClick}
                selected={isSelected(sId)}
                id={sId}
              />
            )
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}
